import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Nav from "react-bootstrap/Nav";
import { createPractice, updateUserId, getDisciplineCodes } from "../../api";
import { getPlacesOfService } from "../../afinanceapi";
import { contractsFromPractice } from "../../utils/tariffRates";
import { jwtDecode } from "jwt-decode";
import Spinner from "react-bootstrap/Spinner";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";

function AddPracModal({ onPracticeUpdate = () => {}, ...props }) {
  const [errors, setErrors] = useState({});
  const [displines, setDisplines] = useState([]);
  const [closeAlert, setCloseAlert] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  const [practice, setPractice] = useState({
    practiceName: "",
    practiceType: "",
    holdingPractice: "",
    practiceNumber: "",
    vatNumber: "",
    practiceEmail: "",
    practicePhoneNumber: "",
    practiceAddress: "",
    practiceAddress2: "",
    practiceCity: "",
    practiceProvince: "",
    practicePostalCode: "",
    practiceWebsite: "",
    practiceDisciplineCode: "",
    practiceDisciplineName: "",
    practiceMedicalType: "",
    hpcsaNumber: "",
    primaryLocationName: "Main Location",
    practicePlaceOfService: "",
    locations: [],
  });
  const [locTab, setLocTab] = useState("rates");
  const [numberingMethod, setNumberingMethod] = useState("sequential");
  const [numberingPrefix, setNumberingPrefix] = useState("");
  const [eraAutoAllocate, setEraAutoAllocate] = useState(true);
  const [eraEnabled, setEraEnabled] = useState(true);
  const [placesOfService, setPlacesOfService] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getPlacesOfService()
      .then((data) => {
        if (cancelled) return;
        setPlacesOfService(Array.isArray(data) ? data : []);
      })
      .catch(() => { if (!cancelled) setPlacesOfService([]); });
    return () => { cancelled = true; };
  }, []);

 const [newLoc, setNewLoc] = useState({
   name: "",
   address1: "",
   address2: "",
   city: "",
   province: "",
   postalCode: "",
   placeOfService: "",
 });

 // Derived preview of the primary location from the address fields
const primaryPreview = {
  name: practice.primaryLocationName || "Main Location",
  address1: practice.practiceAddress || "",
  address2: practice.practiceAddress2 || "",
  city: practice.practiceCity || "",
  province: practice.practiceProvince || "",
  postalCode: practice.practicePostalCode || "",
};


  const addLocation = () => {
   // minimal validation (optional)
   if (!newLoc.name || !newLoc.address1 || !newLoc.city || !newLoc.province || !newLoc.postalCode) {
     showToast("error", "Please fill name, address, city, province and postal code for the new location.");
     return;
   }
   setPractice((prev) => ({
     ...prev,
     locations: [...prev.locations, { ...newLoc }],
   }));
   setNewLoc({ name: "", address1: "", address2: "", city: "", province: "", postalCode: "", placeOfService: "" });
 };

 const removeLocation = (idx) => {
   setPractice((prev) => ({
     ...prev,
     locations: prev.locations.filter((_, i) => i !== idx),
   }));
 };


  useEffect(() => {
    const fetchDisciplineCodes = async () => {
      try {
        const response = await getDisciplineCodes();
        setDisplines(response);
      } catch (error) {
        console.error("Error fetching discipline codes:", error);
      }
    };

    if (props.show) {
      fetchDisciplineCodes();
    }
  }, [props.show]);

  useEffect(() => {
    const selectedType = practice.practiceMedicalType;
    const match = displines.find((d) => d.medicalType === selectedType);
    setAvailableDisciplines(match?.disciplines || []);
  }, [practice.practiceMedicalType, displines]);

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!practice.practiceName)
      newErrors.practiceName = "Practice name is required.";
    if (!practice.practiceNumber)
      newErrors.practiceNumber = "Practice number is required.";
    if (!practice.practiceMedicalType){
      newErrors.practiceMedicalType = "Practice type is required.";
    }
    if (!practice.practiceEmail) newErrors.practiceEmail = "Email is required.";
    if (!practice.practicePhoneNumber)
      newErrors.practicePhoneNumber = "Phone number is required.";
    if (!practice.practiceAddress)
      newErrors.practiceAddress = "Address is required.";
    if (!practice.practiceCity) newErrors.practiceCity = "City is required.";
    if (!practice.practiceProvince)
      newErrors.practiceProvince = "Province is required.";
    if (!practice.practicePostalCode)
      newErrors.practicePostalCode = "Postal code is required.";
    if (!practice.practiceMedicalType)
      newErrors.practiceMedicalType = "A practice type is required.";

    // Field-specific validations
    if (practice.practiceNumber && practice.practiceNumber.length > 13)
      newErrors.practiceNumber = "Practice number cannot exceed 13 characters.";

    if (practice.vatNumber && !/^[4]\d{9}$/.test(practice.vatNumber))
      newErrors.vatNumber = "VAT number must be 10 digits and start with 4.";
    if (
      practice.practicePhoneNumber &&
      !/^\d{3}-\d{3}-\d{4}$/.test(practice.practicePhoneNumber)
    ) {
      newErrors.practicePhoneNumber =
        "Phone number must be in the format xxx-xxx-xxxx and 10 digits long.";
    }
    if (
      practice.practiceMedicalType !== "NM" &&
      practice.practiceMedicalType !== ""
    ) {
      if (!practice.practiceDisciplineCode) {
        newErrors.practiceDisciplineCode = "Practice Discipline is required.";
      }
      if (!practice.hpcsaNumber) {
        newErrors.hpcsaNumber = "Practice Owner HPCSA Number is required.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 async function handleSubmit(e) {
  e.preventDefault();
  if (!validateForm()) return;

  setSubmitting(true);
  try {
    // Build the primary location from the current form fields
    const primaryLocation = {
      name: (practice.primaryLocationName || "Main Location").trim(),
      address1: (practice.practiceAddress || "").trim(),
      address2: (practice.practiceAddress2 || "").trim(),
      city: (practice.practiceCity || "").trim(),
      province: (practice.practiceProvince || "").trim(),
      postalCode: (practice.practicePostalCode || "").trim(),
      placeOfService: practice.practicePlaceOfService || "",
    };

    const payload = {
      ...practice,
      locations: [primaryLocation, ...(practice.locations || [])],
      numberingConfig: {
        method: numberingMethod,
        prefix: numberingMethod === "surname" ? numberingPrefix.trim() : "",
      },
      eraConfig: {
        enabled: eraEnabled,
        autoAllocate: eraAutoAllocate,
      },
    };

    // 1) Create practice
    const res = await createPractice(payload);

    // 2) Link current user to this practice number (your old flow)
    const token = sessionStorage.getItem("user");
    const decoded = jwtDecode(token);
    await updateUserId(decoded._id, practice.practiceNumber);

    // 3) Show success, then close and notify parent
    showToast("success", "Practice added successfully.");
    props.onHide();
    onPracticeUpdate?.();
  } catch (error) {
    console.error("Failed to create practice or update user:", error);
    showToast("error", "Failed to create the practice. Please try again.");
  } finally {
    setSubmitting(false);
  }
}

  function handleChange(e) {
  const { name, value } = e.target;

  if (name === "practicePhoneNumber") {
    const digits = value.replace(/\D/g, "");
    const formatted = digits
      .slice(0, 10)
      .replace(/(\d{3})(\d{3})(\d{1,4})/, "$1-$2-$3");
    setPractice((prev) => ({ ...prev, [name]: formatted }));
  } else if (name === "practiceMedicalType") {
    const updatedFields = {
      [name]: value,
      practiceType: value === "NM" ? "Non-Medical" : "Medical",
    };
    setPractice((prev) => ({ ...prev, ...updatedFields }));
  } else {
    setPractice((prev) => ({ ...prev, [name]: value }));
  }
}


  // Tariff profile flags drive which MedPrax price group invoice lines bill
  // at. Same seeding rule as EditPractice: a toggle writes a full profile so
  // one flag never clobbers the other.
  const effectiveContracts = contractsFromPractice(practice);
  const tariffJsp = effectiveContracts.includes("jsp");
  const tariffNnw = effectiveContracts.includes("nnw");
  const setTariffFlag = (flag) => (e) => {
    const checked = e.target.checked;
    setPractice((prev) => {
      const eff = contractsFromPractice(prev);
      const base = {
        jspMember: eff.includes("jsp"),
        nonContractedUsesNNW: eff.includes("nnw"),
        ...(prev.tariffProfile && typeof prev.tariffProfile === "object"
          ? prev.tariffProfile
          : {}),
      };
      return { ...prev, tariffProfile: { ...base, [flag]: checked } };
    });
  };

  const handleCancelNo = () => {
    setCloseAlert(false); // Hide alert and keep modal open
  };

  const handleCancelYes = async () => {
    setCloseAlert(false);
    props.onHide();
    // Close the modal after successful operation
  };

  return (
    <div>
      <Modal
        {...props}
        onHide={() => setCloseAlert(true)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header
          closeButton={!closeAlert}
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            Add Practice
          </Modal.Title>
        </Modal.Header>
        {closeAlert && (
          <Alert
            variant="info"
            className="d-flex align-items-center justify-content-between mx-0 mb-0 border-0 rounded-0"
          >
            <span>
              Are you sure you want to close? The entered data will be removed.
            </span>
            <div className="ms-auto d-flex gap-2">
              <OverlayTrigger placement="top" overlay={<Tooltip>Discard and close</Tooltip>}>
                <Button variant="outline-primary" onClick={handleCancelYes}>
                  <i className="fa-solid fa-check" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>Keep editing</Tooltip>}>
                <Button variant="outline-secondary" onClick={handleCancelNo}>
                  <i className="fa-solid fa-xmark" />
                </Button>
              </OverlayTrigger>
            </div>
          </Alert>
        )}
        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="practiceName">
                <Form.Label>Practice Name</Form.Label>
                <Form.Control
                  placeholder=""
                  name="practiceName"
                  onChange={handleChange}
                  isInvalid={!!errors.practiceName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.practiceName}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md={4} controlId="checkboxGroup">
                <div className="d-flex flex-column">
                  <Form.Check
                    disabled
                    type="checkbox"
                    label="Main Practice"
                    name="holdingPractice"
                    onChange={handleChange}
                    className="mb-2 mt-3"
                  />
                  <Form.Check
                    type="checkbox"
                    label="Non-Medical Practice"
                    name="practiceType"
                    checked={practice.practiceMedicalType === "NM"}
                    disabled
                  />
                </div>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="practiceNumber">
                <Form.Label>Practice Number</Form.Label>
                <Form.Control
                  placeholder=""
                  name="practiceNumber"
                  onChange={handleChange}
                  isInvalid={!!errors.practiceNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.practiceNumber}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} controlId="vatNumber">
                <Form.Label>VAT Number</Form.Label>
                <Form.Control
                  placeholder=""
                  name="vatNumber"
                  onChange={handleChange}
                  isInvalid={!!errors.vatNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.vatNumber}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col}>
                <Form.Label>Practice Type</Form.Label>
                <Form.Select
                  defaultValue="Choose..."
                  name="practiceMedicalType"
                  onChange={(e) => {
                    const value = e.target.value;
                    let type = "";
                    if (value.includes("Medical Practitioners")) type = "MP";
                    else if (value.includes("Dental Practitioners"))
                      type = "DP";
                    else if (value.includes("Allied Practitioners"))
                      type = "AP";
                    else if (value.includes("Non-Medical")) type = "NM";

                    // Trigger your change handler with remapped value
                    handleChange({
                      target: { name: "practiceMedicalType", value: type },
                    });
                  }}
                  isInvalid={!!errors.practiceMedicalType}
                >
                  <option>Choose...</option>
                  <option>Medical Practitioners GP</option>
                  <option>Medical Practitioners Specialist</option>
                  <option>Dental Practitioners</option>
                  <option>Allied Practitioners</option>
                  <option>Non-Medical</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.practiceMedicalType}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row>
              {availableDisciplines && availableDisciplines.length > 0 && (
                <>
                  <Form.Group as={Col} className="mb-3">
                    <Form.Label>Discipline</Form.Label>
                    <Form.Select
                      name="practiceDisciplineCode"
                      isInvalid={!!errors.practiceDisciplineCode}
                      onChange={(e) => {
                        const selectedCode = e.target.value;
                        const selected = availableDisciplines.find(
                          (d) => d.disciplineCode === selectedCode
                        );
                        handleChange({
                          target: {
                            name: "practiceDisciplineCode",
                            value: selected?.disciplineCode || "",
                          },
                        });
                        handleChange({
                          target: {
                            name: "practiceDisciplineName",
                            value: selected?.disciplineName || "",
                          },
                        });
                      }}
                    >
                      <option>Choose...</option>
                      {availableDisciplines.map((d, idx) => (
                        <option key={idx} value={d.disciplineCode}>
                          {d.disciplineName}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.practiceDisciplineCode}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group as={Col} controlId="practicehpcsa">
                    <Form.Label>Practice Owner HPCSA</Form.Label>
                    <Form.Control
                      className="mb-3"
                      placeholder=""
                      name="hpcsaNumber"
                      onChange={handleChange}
                      isInvalid={!!errors.hpcsaNumber}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.hpcsaNumber}
                    </Form.Control.Feedback>
                  </Form.Group>
                </>
              )}
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="practicePhoneNumber">
                <Form.Label>Practice Phone Number</Form.Label>
                <Form.Control
                  placeholder="xxx-xxx-xxxx"
                  name="practicePhoneNumber"
                  value={practice.practicePhoneNumber}
                  onChange={handleChange}
                  isInvalid={!!errors.practicePhoneNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.practicePhoneNumber}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} controlId="practiceEmail">
                <Form.Label>Practice Email</Form.Label>
                <Form.Control
                  placeholder=""
                  name="practiceEmail"
                  onChange={handleChange}
                  isInvalid={!!errors.practiceEmail}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.practiceEmail}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="practiceWebsite">
                <Form.Label>Practice Website</Form.Label>
                <Form.Control
                  placeholder=""
                  name="practiceWebsite"
                  onChange={handleChange}
                  isInvalid={!!errors.practiceWebsite}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.practiceWebsite}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            <Row className="mb-3">
<Form.Group as={Col} controlId="primaryLocationName">
   <Form.Label>Primary Location Name</Form.Label>
   <Form.Control
     placeholder="e.g. Main Rooms"
     name="primaryLocationName"
     value={practice.primaryLocationName}
     onChange={handleChange}
   />
 </Form.Group>
 <Form.Group as={Col} controlId="practicePlaceOfService">
   <Form.Label>Place of Service (Primary)</Form.Label>
   <Form.Select
     value={practice.practicePlaceOfService || ""}
     onChange={(e) =>
       setPractice((prev) => ({ ...prev, practicePlaceOfService: e.target.value }))
     }
   >
     <option value="">Select a place of service...</option>
     {placesOfService.map((pos) => (
       <option key={pos.HCFA_code} value={pos.HCFA_code}>
         {pos.HCFA_code} - {pos.newDescription}
       </option>
     ))}
   </Form.Select>
 </Form.Group>
</Row>


            <Row className="mb-3">
              <Form.Group as={Col} controlId="formGridAddress1">
                <Form.Label>Practice Address</Form.Label>
                <Form.Control
                  placeholder=""
                  name="practiceAddress"
                  onChange={handleChange}
                  isInvalid={!!errors.practiceAddress}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.practiceAddress}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} controlId="formGridAddress2">
                <Form.Label>Practice Address 2</Form.Label>
                <Form.Control
                  placeholder=""
                  name="practiceAddress2"
                  onChange={handleChange}
                />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="formGridCity">
                <Form.Label>City</Form.Label>
                <Form.Control
                  name="practiceCity"
                  onChange={handleChange}
                  isInvalid={!!errors.practiceCity}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.practiceCity}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} controlId="formGridState">
                <Form.Label>Province</Form.Label>
                <Form.Select
                  defaultValue="Choose..."
                  name="practiceProvince"
                  onChange={handleChange}
                  isInvalid={!!errors.practiceProvince}
                >
                  <option>Choose...</option>
                  <option>Eastern Cape</option>
                  <option>Free State</option>
                  <option>Gauteng</option>
                  <option>KwaZulu-Natal</option>
                  <option>Limpopo</option>
                  <option>Mpumalanga</option>
                  <option>North West</option>
                  <option>Northern Cape</option>
                  <option>Western Cape</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.practiceProvince}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} controlId="formGridZip">
                <Form.Label>Postal Code</Form.Label>
                <Form.Control
                  name="practicePostalCode"
                  onChange={handleChange}
                  isInvalid={!!errors.practicePostalCode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.practicePostalCode}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            {/* --- Locations / Numbering tabs -------------------------------- */}
<hr className="mt-3 mb-3" />
<Nav variant="tabs" activeKey={locTab} onSelect={setLocTab} className="mb-3">
  <Nav.Item>
    <Nav.Link eventKey="rates">Medical Aid Rates</Nav.Link>
  </Nav.Item>
  <Nav.Item>
    <Nav.Link eventKey="eras">ERAs</Nav.Link>
  </Nav.Item>
  <Nav.Item>
    <Nav.Link eventKey="numbering">Numbering</Nav.Link>
  </Nav.Item>
  <Nav.Item>
    <Nav.Link eventKey="locations">Additional Locations</Nav.Link>
  </Nav.Item>
</Nav>

{locTab === "rates" && (
  <>
    {(!practice.practiceMedicalType ||
      practice.practiceMedicalType === "NM") && (
      <p className="text-muted small mb-3">
        Medical aid rate settings apply to medical practices only.
      </p>
    )}
            {practice.practiceMedicalType &&
              practice.practiceMedicalType !== "NM" && (
                <Row className="mb-3">
                  <Col xs={12}>
                    <Form.Check
                      type="checkbox"
                      id="setup-tariff-nnw"
                      label="Not contracted: bill Non-Network (NNW) rates where the scheme publishes them"
                      checked={tariffNnw}
                      onChange={setTariffFlag("nonContractedUsesNNW")}
                    />
                    {practice.practiceDisciplineCode === "072" && (
                      <Form.Check
                        type="checkbox"
                        id="setup-tariff-jsp"
                        label="Discovery Joint Sustainability Project (JSP) member"
                        checked={tariffJsp}
                        onChange={setTariffFlag("jspMember")}
                      />
                    )}
                    <small className="text-muted">
                      Decides which price group invoice lines are billed at.
                      Scheme rate (MSR) is always the fallback.
                    </small>
                  </Col>
                </Row>
              )}

  </>
)}

{locTab === "locations" && (
  <>
    <Row className="mb-2">
      <Form.Group as={Col} md={4} controlId="locName">
        <Form.Label>Location Name</Form.Label>
        <Form.Control
          placeholder="e.g. Branch – Sandton"
          value={newLoc.name}
          onChange={(e) => setNewLoc((p) => ({ ...p, name: e.target.value }))}
        />
      </Form.Group>
      <Form.Group as={Col} md={8} controlId="locAddress1">
        <Form.Label>Address 1</Form.Label>
        <Form.Control
          value={newLoc.address1}
          onChange={(e) => setNewLoc((p) => ({ ...p, address1: e.target.value }))}
        />
      </Form.Group>
    </Row>

    <Row className="mb-2">
      <Form.Group as={Col} md={6} controlId="locAddress2">
        <Form.Label>Address 2</Form.Label>
        <Form.Control
          value={newLoc.address2}
          onChange={(e) => setNewLoc((p) => ({ ...p, address2: e.target.value }))}
        />
      </Form.Group>
      <Form.Group as={Col} md={6} controlId="locCity">
        <Form.Label>City</Form.Label>
        <Form.Control
          value={newLoc.city}
          onChange={(e) => setNewLoc((p) => ({ ...p, city: e.target.value }))}
        />
      </Form.Group>
    </Row>

    <Row className="mb-2">
      <Form.Group as={Col} md={6} controlId="locProvince">
        <Form.Label>Province</Form.Label>
        <Form.Select
          value={newLoc.province}
          onChange={(e) => setNewLoc((p) => ({ ...p, province: e.target.value }))}
        >
          <option value="">Choose...</option>
          <option>Eastern Cape</option>
          <option>Free State</option>
          <option>Gauteng</option>
          <option>KwaZulu-Natal</option>
          <option>Limpopo</option>
          <option>Mpumalanga</option>
          <option>North West</option>
          <option>Northern Cape</option>
          <option>Western Cape</option>
        </Form.Select>
      </Form.Group>
      <Form.Group as={Col} md={6} controlId="locPostal">
        <Form.Label>Postal Code</Form.Label>
        <Form.Control
          value={newLoc.postalCode}
          onChange={(e) => setNewLoc((p) => ({ ...p, postalCode: e.target.value }))}
        />
      </Form.Group>
    </Row>

    <Row className="mb-2">
      <Form.Group as={Col} md={12} controlId="locPlaceOfService">
        <Form.Label>Place of Service</Form.Label>
        <Form.Select
          value={newLoc.placeOfService}
          onChange={(e) => setNewLoc((p) => ({ ...p, placeOfService: e.target.value }))}
        >
          <option value="">Select a place of service...</option>
          {placesOfService.map((pos) => (
            <option key={pos.HCFA_code} value={pos.HCFA_code}>
              {pos.HCFA_code} - {pos.newDescription}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    </Row>

    <div className="mb-3">
      <Button variant="outline-primary" size="sm" onClick={addLocation}>
        <i className="fa-solid fa-plus me-2" />
        Add Location
      </Button>
    </div>

    {/* Primary location preview */}
    <div className="border rounded p-2 mb-3 small">
      <div className="fw-semibold text-primary">{primaryPreview.name}</div>
      <div>{primaryPreview.address1 || <span className="text-muted">Address 1</span>}</div>
      {primaryPreview.address2 && <div>{primaryPreview.address2}</div>}
      <div>
        {primaryPreview.city || <span className="text-muted">City</span>}
        {primaryPreview.province && `, ${primaryPreview.province}`}{" "}
        {primaryPreview.postalCode}
      </div>
    </div>

    {practice.locations.length > 0 && (
      <div className="mb-3">
        {practice.locations.map((loc, idx) => (
          <div
            key={`${loc.name}-${idx}`}
            className="border rounded p-2 mb-2 d-flex justify-content-between align-items-start"
          >
            <div className="small">
              <div className="fw-semibold text-primary">{loc.name}</div>
              <div>{loc.address1}</div>
              {loc.address2 && <div>{loc.address2}</div>}
              <div>
                {loc.city}, {loc.province} {loc.postalCode}
              </div>
              {loc.placeOfService && (
                <div className="text-muted mt-1">
                  POS: {loc.placeOfService}
                  {(() => {
                    const match = placesOfService.find(
                      (pos) => pos.HCFA_code === loc.placeOfService
                    );
                    return match ? ` - ${match.newDescription}` : "";
                  })()}
                </div>
              )}
            </div>
            <OverlayTrigger placement="top" overlay={<Tooltip>Remove location</Tooltip>}>
              <Button
                variant="link"
                size="sm"
                className="text-danger text-decoration-none p-0"
                onClick={() => removeLocation(idx)}
                aria-label="Remove location"
              >
                <i className="fa-solid fa-trash"></i>
              </Button>
            </OverlayTrigger>
          </div>
        ))}
      </div>
    )}
  </>
)}

{locTab === "numbering" && (
  <div className="mb-3">
    <p className="text-muted small mb-3">
      Choose how account and patient numbers are generated for this practice.
    </p>
    {[
      { value: "custom", label: "Custom (manual entry)", desc: "You enter the number manually for each patient." },
      { value: "sequential", label: "Sequential (ACC/PAT)", desc: "Numbers auto-increment: ACC00001, PAT00001, etc." },
      { value: "surname", label: "Surname-based", desc: "Numbers use the patient's surname code, e.g. DLA0001 or PHYDLA0001 with a prefix." },
    ].map(({ value, label, desc }) => (
      <Form.Check
        key={value}
        type="radio"
        id={`method-${value}`}
        label={<><strong>{label}</strong><br /><span className="text-muted small">{desc}</span></>}
        value={value}
        checked={numberingMethod === value}
        onChange={() => setNumberingMethod(value)}
        className="mb-2"
      />
    ))}
    {numberingMethod === "surname" && (
      <Form.Group className="mt-3" style={{ maxWidth: 220 }}>
        <Form.Label>Prefix <span className="text-muted small fw-normal">(optional)</span></Form.Label>
        <Form.Control
          placeholder="e.g. PHY"
          value={numberingPrefix}
          maxLength={6}
          onChange={(e) => setNumberingPrefix(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
        />
        <Form.Text className="text-muted">
          Leave empty to use surname code only (e.g. DLA0001).
        </Form.Text>
      </Form.Group>
    )}
  </div>
)}

{locTab === "eras" && (
  <div className="mb-3">
    <p className="text-muted small mb-3">
      Choose how incoming ERAs (electronic remittance advices) are processed
      for this practice.
    </p>
    <Form.Check
      type="switch"
      id="setup-era-enabled"
      className="mb-3"
      label={
        <>
          <strong>Practice receives ERAs</strong>
          <br />
          <span className="text-muted small">
            Turn off for a practice that does not subscribe to electronic
            remittance advices. The ERA matcher and unmatched-ERA indicators
            are hidden on its accounts.
          </span>
        </>
      }
      checked={eraEnabled}
      onChange={(e) => setEraEnabled(e.target.checked)}
    />
    <Form.Check
      type="switch"
      id="setup-era-auto-allocate"
      disabled={!eraEnabled}
      label={
        <>
          <strong>Automatically allocate ERAs</strong>
          <br />
          <span className="text-muted small">
            Incoming ERAs are matched to submitted claims and receipts are
            created automatically. Claims that cannot be matched appear on the
            account as unmatched.
          </span>
        </>
      }
      checked={eraAutoAllocate}
      onChange={(e) => setEraAutoAllocate(e.target.checked)}
    />
    {!eraAutoAllocate && (
      <div className="text-muted small mt-2">
        ERAs will only be recorded and listed under the ERA section: no
        matching, receipts or invoice updates happen. Payments are captured
        manually.
      </div>
    )}
  </div>
)}


          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting}
            style={{ minWidth: 140 }}
          >
            {submitting ? <Spinner size="sm" animation="border" /> : "Add Practice"}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => setCloseAlert(true)}
            style={{ minWidth: 140 }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </div>
  );
}

export function SetUpPractice({ onPracticeUpdate }) {
  const [modalShow, setModalShow] = useState(false);

  return (
    <div>
      <Button
        variant="primary"
        className="w-100"
        onClick={() => setModalShow(true)}
      >
        Add Practice
      </Button>
      <AddPracModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        onPracticeUpdate={onPracticeUpdate}
      />
    </div>
  );
}
